import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { apiError, fmtDate } from '../../lib/apiError';

const Stars = ({ n, size = 'w-4 h-4' }) => (
  <div className="flex">{[1, 2, 3, 4, 5].map((i) => <Star key={`star-${i}`} className={`${size} ${i <= n ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}</div>
);

const TeacherRatings = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/teacher-portal/ratings`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e, 'Failed to load ratings')));
  }, []);

  if (!data) return <p className="text-center text-slate-500 py-20">Loading...</p>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="teacher-ratings-page">
      <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-8">Ratings & Reviews</h1>
      <div className="grid md:grid-cols-3 gap-6 mb-10">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6 text-center">
            <p className="text-5xl font-bold text-slate-900" data-testid="teacher-ratings-average">{Number(data.average || 0).toFixed(1)}</p>
            <div className="flex justify-center my-2"><Stars n={Math.round(data.average || 0)} size="w-5 h-5" /></div>
            <p className="text-sm text-slate-500" data-testid="teacher-ratings-total">{data.total} reviews</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg md:col-span-2">
          <CardContent className="p-6 space-y-2">
            {[5, 4, 3, 2, 1].map((n) => {
              const count = data.distribution[String(n)] || 0;
              const pct = data.total ? (count / data.total) * 100 : 0;
              return (
                <div key={`dist-${n}`} className="flex items-center gap-3 text-sm">
                  <span className="w-6 text-slate-600">{n}★</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${pct}%` }} /></div>
                  <span className="w-6 text-right text-slate-500">{count}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
      {data.ratings.length === 0 ? (
        <p className="text-center text-slate-500 py-10" data-testid="teacher-no-ratings">No reviews yet. They'll appear here after students rate completed sessions.</p>
      ) : (
        <div className="space-y-3">
          {data.ratings.map((r) => (
            <Card key={r.id} className="border-0 shadow" data-testid={`teacher-rating-${r.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <Stars n={r.rating} />
                  <span className="text-xs text-slate-400">{r.booking_date ? fmtDate(r.booking_date) : ''}</span>
                </div>
                <p className="text-sm text-slate-500">{r.student_name}{r.subject && ` · ${r.subject}`}</p>
                {r.review && <p className="text-slate-700 mt-2">{r.review}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeacherRatings;
